import { useCallback, useMemo, useState } from 'react'
import { MAX_COL_PX, MIN_COL_PX } from './columnWidths'

/**
 * The grid's LAYOUT state (#2375 §11: `pinnedCols`, `widths`) — the one
 * implementation every unified grid uses for "Pin column left" and "Autosize".
 *
 * `DataGrid` DRAWS a layout (`pinnedColumns`, `widthOf`) and MEASURES one
 * (`renderColumnMenu`'s `autosize`); it does not own it, because where a layout
 * lives differs by host: a Console root persists it for the person, a Portal
 * list and a nested relationship keep it for the session. What must NOT differ
 * is what pinning and sizing MEAN, so the rules live here, once:
 *
 *   · pinning appends to the END of the pinned run (pin order is reading order);
 *     unpinning removes only that column; a key is never pinned twice;
 *   · a width is clamped to the same bounds a drag is (`MIN_COL_PX`/`MAX_COL_PX`).
 */
/** Pin or unpin one column. Pure. */
export function togglePinnedColumn(pinned: readonly string[], key: string): string[] {
  if (pinned.includes(key)) return pinned.filter((k) => k !== key)
  return [...pinned, key]
}

export function clampColumnWidth(px: number): number {
  return Math.max(MIN_COL_PX, Math.min(MAX_COL_PX, Math.round(px)))
}

export interface GridLayoutState {
  pinnedColumns: readonly string[]
  togglePinned: (key: string) => void
  widthOf: (key: string) => number | undefined
  setWidth: (key: string, px: number) => void
  resetWidth: (key: string) => void
}

/**
 * Session-scoped layout, keyed by `scope` — a grid's stable identity (its
 * entity, view and path; never a record UUID alone). Switching scope shows that
 * scope's own layout; returning to a scope restores it.
 */
export function useGridLayoutState(scope: string): GridLayoutState {
  const [byScope, setByScope] = useState<Record<string, { pinned: string[]; widths: Record<string, number> }>>({})
  const current = byScope[scope] ?? { pinned: [], widths: {} }
  const patch = useCallback((next: (s: { pinned: string[]; widths: Record<string, number> }) => { pinned: string[]; widths: Record<string, number> }) =>
    setByScope((all) => ({ ...all, [scope]: next(all[scope] ?? { pinned: [], widths: {} }) })), [scope])
  return useMemo(() => ({
    pinnedColumns: current.pinned,
    togglePinned: (key) => patch((s) => ({ ...s, pinned: togglePinnedColumn(s.pinned, key) })),
    widthOf: (key) => current.widths[key],
    setWidth: (key, px) => patch((s) => ({ ...s, widths: { ...s.widths, [key]: clampColumnWidth(px) } })),
    resetWidth: (key) => patch((s) => {
      const widths = { ...s.widths }
      delete widths[key]
      return { ...s, widths }
    }),
  }), [current, patch])
}
